import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { ResourceType } from '@/types';
import { useMutation } from '@/utils/api';

/**
 * Transforms technical backend errors to user-friendly messages.
 * Handles preseed type mismatches and strips internal job key formats.
 *
 * @param backendError - The raw error message from the backend
 * @returns User-friendly error message
 */
export function transformCapabilityError(backendError: string): string {
  // Pattern: "expected preseed class 'X', got 'Y'"
  const preseedMismatch = backendError.match(
    /expected preseed class '(\w+)', got '(\w+)'/
  );

  if (preseedMismatch) {
    const [, expected, actual] = preseedMismatch;

    if (expected === 'cik' && actual === 'edgar') {
      return 'This capability requires CIK (company ID) preseeds. You selected company name preseeds. Try using the "collect-edgar" capability instead.';
    }

    // Generic preseed type mismatch
    return `This capability requires ${expected} preseeds, but you selected ${actual} preseeds. Please select the correct preseed type.`;
  }

  // Strip internal job key format from error (e.g., "#job#capability#target#hash:")
  const cleanedError = backendError.replace(/#\w+#[^:]+:\s*/g, '').trim();

  return cleanedError || 'An error occurred';
}

export function useBulkCapabilityJob() {
  const axios = useAxios();

  const { invalidate: invalidateWebCrawler } = useGenericSearch(
    {
      label: ResourceType.Attribute,
      query: `#attribute#status#crawler`,
    },
    { enabled: false }
  );

  const { invalidate: invalidateWebCrawlerJobs } = useGenericSearch({
    label: ResourceType.Job,
    query: 'source:crawler',
  });

  return useMutation({
    defaultErrorMessage: 'Failed to run jobs',
    mutationFn: async (
      jobs: {
        assetKey: string;
        capabilities?: string[];
        config?: Record<string, string>;
      }[]
    ) => {
      const capabilityNames = {
        portscan: 'port scanner',
        crawler: 'web crawler',
        nuclei: 'network scanner',
      };

      // Check if any job has capabilities defined
      const hasCapabilities = jobs.some(job => job.capabilities?.length);

      // If no capabilities are defined, it's a comprehensive scan
      const scanType = !hasCapabilities
        ? 'comprehensive scans'
        : jobs[0]?.capabilities
            ?.map(
              cap => capabilityNames[cap as keyof typeof capabilityNames] || cap
            )
            .join(', ');

      const loadingToastId = jobs[0]?.assetKey || 'scan-loading';
      toast.loading(`Starting ${scanType ?? 'comprehensive scans'}...`, {
        id: loadingToastId,
      });

      const promises = jobs
        .map(async ({ assetKey, capabilities, config }) => {
          const payload = {
            key: assetKey,
            capabilities,
            config,
          };
          const { data } = await axios.post(`/job/`, payload);

          return data;
        })
        .map(promise =>
          promise.catch(error => {
            console.error('[useCapability] Job request failed:', {
              error: error,
              response: error.response?.data,
              status: error.response?.status,
            });
            return error;
          })
        );

      const response = await Promise.all(promises);

      // Detect errors more robustly - check for axios error properties or Error instances
      const isError = (result: unknown): boolean => {
        if (result instanceof Error) return true;
        if (result && typeof result === 'object') {
          const obj = result as Record<string, unknown>;
          // Check for axios error properties
          if (obj.isAxiosError) return true;
          // Check for error response structure
          if (obj.response && typeof obj.response === 'object') return true;
        }
        return false;
      };

      const validResults = response.filter(result => !isError(result));
      const failedResults = response.filter(result => isError(result));

      if (validResults.length > 0) {
        // Show success message, with warning if some failed
        if (failedResults.length > 0) {
          // Covers both common failure causes: the backend scan-status gate
          // (HTTP 400 for frozen/rejected/deleted/pending targets) and
          // capability misconfiguration.
          toast.warning(
            `Started ${scanType ?? 'scans'} on ${validResults.length} asset${validResults.length > 1 ? 's' : ''}, but ${failedResults.length} failed — the target may be frozen, rejected, deleted, or pending approval, or the capability may be misconfigured`,
            {
              id: loadingToastId,
              duration: 5000,
            }
          );
        } else {
          const assetCount = validResults.length;
          toast.success(
            `✓ Scans started on ${assetCount} asset${assetCount > 1 ? 's' : ''}! Status will update automatically as scans progress.`,
            {
              id: loadingToastId,
              duration: 6000,
            }
          );
        }

        queryClient.invalidateQueries({
          queryKey: getQueryKey.getMy(ResourceType.Job),
        });
        queryClient.invalidateQueries({
          queryKey: getQueryKey.genericSearch(''),
        });
        if (scanType === 'web crawler') {
          invalidateWebCrawler();
          invalidateWebCrawlerJobs();
        }

        // Enable polling for 2 minutes after scan submission to catch status updates
        // This ensures the UI updates when scans start processing
        const pollingInterval = setInterval(() => {
          queryClient.invalidateQueries({
            queryKey: getQueryKey.genericSearch(''),
          });
        }, 3000); // Poll every 3 seconds

        // Stop polling after 2 minutes
        setTimeout(() => {
          clearInterval(pollingInterval);
        }, 120000);
      } else if (failedResults.length > 0) {
        // All jobs failed - extract error message from axios error or Error instance
        const firstError = failedResults[0];
        let errorMessage = 'Failed to start scans';
        if (firstError && typeof firstError === 'object') {
          const err = firstError as Record<string, unknown>;
          if (err.response && typeof err.response === 'object') {
            const response = err.response as Record<string, unknown>;
            if (typeof response.data === 'string') {
              errorMessage = response.data;
            }
          } else if (err.message && typeof err.message === 'string') {
            errorMessage = err.message;
          }
        }
        // Transform technical errors to user-friendly messages
        const userFriendlyMessage = transformCapabilityError(errorMessage);
        toast.error(`Failed to start scans: ${userFriendlyMessage}`, {
          id: loadingToastId,
          duration: 5000,
        });
        // All attempted jobs failed - reject so callers (e.g. the WHOIS refresh
        // flow) stop their spinner and skip their success side-effects. The
        // useMutation wrapper rethrows via getQueryError without showing a toast,
        // and this hook has no onError, so there is no double toast.
        throw firstError ?? new Error('Failed to start scans');
      }

      return response;
    },
  });
}
